import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Radio as RadioIcon,
  List as ListIcon
} from '@mui/icons-material';
import { radioAPI } from '../services/api';

// Компонент карточки радио-плейлиста для мобильных устройств
const RadioPlaylistCard = ({ playlist, onEdit, onDelete, onViewChannels }) => {
  return (
    <Card sx={{ 
      mb: 2, 
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      opacity: playlist.is_active ? 1 : 0.6
    }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Первая строка: основная информация */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 1,
          width: '100%'
        }}>
          {/* ID */}
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            fontSize: '0.8rem', 
            fontWeight: 600,
            minWidth: '24px',
            textAlign: 'center'
          }}>
            #{playlist.id}
          </Typography>

          {/* Название */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ 
              fontSize: '0.9rem', 
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.25
            }}>
              {playlist.name}
            </Typography>
            <Typography variant="caption" sx={{ 
              color: 'text.secondary',
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}>
              {playlist.description || 'Нет описания'}
            </Typography>
          </Box>

          {/* Статус */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
            {playlist.is_default && (
              <Chip label="По умолчанию" color="primary" size="small" sx={{ fontSize: '0.7rem' }} />
            )}
            <Chip 
              label={playlist.is_active ? 'Активен' : 'Неактивен'} 
              color={playlist.is_active ? 'success' : 'default'} 
              size="small" 
              sx={{ fontSize: '0.7rem' }} 
            />
          </Box>
        </Box>

        {/* Вторая строка: дата создания и управление */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%',
          minHeight: '44px'
        }}>
          {/* Дата создания */}
          <Typography variant="caption" sx={{ 
            color: 'text.secondary',
            fontSize: '0.75rem'
          }}>
            Создан: {new Date(playlist.created_at).toLocaleDateString('ru-RU')}
          </Typography>

          {/* Кнопки управления (touch-friendly) */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <IconButton
              size="medium"
              onClick={() => onViewChannels(playlist.id)}
              title="Просмотр каналов"
              sx={{ 
                minWidth: '44px', 
                minHeight: '44px',
                backgroundColor: 'info.light',
                color: 'info.contrastText',
                '&:hover': { backgroundColor: 'info.main' }
              }}
            >
              <ListIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            <IconButton
              size="medium"
              onClick={() => onEdit(playlist)}
              title="Редактировать"
              sx={{ 
                minWidth: '44px', 
                minHeight: '44px',
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                '&:hover': { backgroundColor: 'primary.main' }
              }}
            >
              <EditIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            {!playlist.is_default && (
              <IconButton
                size="medium"
                onClick={() => onDelete(playlist)}
                title="Удалить"
                sx={{ 
                  minWidth: '44px', 
                  minHeight: '44px',
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                  '&:hover': { backgroundColor: 'error.main' }
                }}
              >
                <DeleteIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const RadioPlaylistsPage = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    is_default: false
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, playlist: null });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    loadPlaylists();
  }, []);

  const loadPlaylists = async () => {
    try {
      setLoading(true);
      const response = await radioAPI.getPlaylists();
      setPlaylists(response.data);
    } catch (error) {
      console.error('Ошибка загрузки радио плейлистов:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка загрузки радио плейлистов',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (playlist = null) => {
    if (playlist) {
      setEditingPlaylist(playlist);
      setFormData({
        name: playlist.name || '',
        description: playlist.description || '',
        is_active: playlist.is_active ?? true,
        is_default: playlist.is_default ?? false
      });
    } else {
      setEditingPlaylist(null);
      setFormData({
        name: '',
        description: '',
        is_active: true,
        is_default: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPlaylist(null);
    setFormData({
      name: '',
      description: '',
      is_active: true,
      is_default: false
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingPlaylist) {
        await radioAPI.updatePlaylist(editingPlaylist.id, formData);
        setSnackbar({
          open: true,
          message: 'Радио плейлист обновлен',
          severity: 'success'
        });
      } else {
        await radioAPI.createPlaylist(formData);
        setSnackbar({
          open: true,
          message: 'Радио плейлист создан',
          severity: 'success'
        });
      }
      handleCloseDialog();
      loadPlaylists();
    } catch (error) {
      console.error('Ошибка сохранения радио плейлиста:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка сохранения радио плейлиста',
        severity: 'error'
      });
    }
  };

  const handleDelete = (playlist) => {
    if (playlist.is_default) {
      setSnackbar({
        open: true,
        message: 'Нельзя удалить плейлист по умолчанию',
        severity: 'warning'
      });
      return;
    }

    setDeleteDialog({ open: true, playlist });
  };

  const handleConfirmDelete = async () => {
    const { playlist } = deleteDialog;
    if (!playlist) return;

    try {
      await radioAPI.deletePlaylist(playlist.id);
      setSnackbar({
        open: true,
        message: 'Радио плейлист удален',
        severity: 'success'
      });
      loadPlaylists();
    } catch (error) {
      console.error('Ошибка удаления радио плейлиста:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка удаления радио плейлиста',
        severity: 'error'
      });
    } finally {
      setDeleteDialog({ open: false, playlist: null });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialog({ open: false, playlist: null });
  };

  const handleViewChannels = (playlist) => {
    window.location.href = `/radio/playlists/${playlist.id}/channels`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RadioIcon />
          Радио плейлисты
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size={isMobile ? 'small' : 'medium'}
        >
          {isMobile ? 'Добавить' : 'Добавить плейлист'}
        </Button>
      </Box>

      {/* Адаптивное отображение плейлистов: карточки на мобильных, таблица на десктопе */}
      {isMobile ? (
        /* МОБИЛЬНАЯ ВЕРСИЯ - КАРТОЧКИ */
        <Box sx={{ 
          width: '100%', 
          maxWidth: '100vw', 
          overflow: 'hidden'
        }}>
          {playlists.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Плейлисты не найдены
              </Typography>
            </Paper>
          ) : (
            playlists.map((playlist) => (
              <RadioPlaylistCard
                key={playlist.id}
                playlist={playlist}
                onEdit={handleOpenDialog}
                onDelete={handleDelete}
                onViewChannels={handleViewChannels}
              />
            ))
          )}
        </Box>
      ) : (
        /* ДЕСКТОПНАЯ ВЕРСИЯ - ТАБЛИЦА */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Описание</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Дата создания</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {playlists.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Плейлисты не найдены
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                playlists.map((playlist) => (
                  <TableRow 
                    key={playlist.id}
                    sx={{ 
                      opacity: playlist.is_active ? 1 : 0.6,
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell>#{playlist.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {playlist.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {playlist.description || 'Нет описания'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {playlist.is_default && (
                          <Chip label="По умолчанию" color="primary" size="small" />
                        )}
                        <Chip 
                          label={playlist.is_active ? 'Активен' : 'Неактивен'} 
                          color={playlist.is_active ? 'success' : 'default'} 
                          size="small" 
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(playlist.created_at).toLocaleDateString('ru-RU')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewChannels(playlist)}
                          title="Просмотр каналов"
                          color="info"
                        >
                          <ListIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(playlist)}
                          title="Редактировать"
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        {!playlist.is_default && (
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(playlist)}
                            title="Удалить"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Диалог создания/редактирования */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingPlaylist ? 'Редактировать радио плейлист' : 'Создать радио плейлист'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Описание"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label="Активен"
            sx={{ mb: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
              />
            }
            label="По умолчанию"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPlaylist ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить радио плейлист "{deleteDialog.playlist?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Отмена</Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RadioPlaylistsPage;
